import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import TableSection from "../tables/TableSection";
import styles from "../../styles/users.module.scss";
import Search from "antd/es/transfer/search";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { useDebounce } from "../../hooks/useDebounce";
import agentService from "../../api/agent-api";
import { hardDeleteUser } from "../../api/user-api";
import { formatPriceWithCurrency } from "../../utils/formatPrice";
import CreateAgentModal from "../modals/CreateAgentModal";
import {Button, Dropdown} from "antd";
import Spinner from "../elements/Spinner/Spinner";
import BlockUserModal from "../modals/BlockUserModal";
import DeleteModal from "../modals/DeleteModal";
import { usePermissions } from "../../context/PermissionsContext";
import {
  adminAwardLoyaltyPoints,
  adminDeductLoyaltyPoints,
} from "../../api/loyalty-api";
import AwardLoyaltyPointsModal from "../modals/AwardLoyaltyPointsModal";
import DeductLoyaltyPointsModal from "../modals/DeductLoyaltyPointsModal";

const statusPill = (status) => {
    const isActive = status === 'ACTIVE';
    return (
        <span className={isActive ? 'active' : 'inactive'}>
            {isActive ? 'Aktivan' : status === 'SUSPENDED' ? 'Suspendovan' : 'Deaktiviran'}
        </span>
    );
};

const tableColumns = [
    {
        key: 'firstName',
        label: 'Agent',
        render: (_, row) => `${row.firstName || ''} ${row.lastName || ''}`,
    },
    {
        key: 'email',
        label: 'Email',
        render: (val) => val,
    },
    {
        key: 'referralCode',
        label: 'Referral kod',
        render: (val) => <span style={{ color: '#005844' }}>{val || '-'}</span>,
    },
    {
        key: 'createdAt',
        label: 'Datum',
        render: (val) => val ? dayjs(val).format('DD.MM.YYYY.') : '-',
    },
    // Commission-percentage pill disabled (2026-10-13): hidden from the agents list per request.
    // {
    //     key: 'commissionPercent',
    //     label: 'Provizija',
    //     render: (val) => (
    //         <span
    //             style={{
    //                 padding: '4px 12px',
    //                 borderRadius: '16px',
    //                 fontSize: '14px',
    //                 fontWeight: '500',
    //                 display: 'inline-block',
    //                 backgroundColor: '#ECFDF3',
    //                 color: '#027A48',
    //             }}
    //         >
    //             {`Provizija ${val ?? 0}%`}
    //         </span>
    //     ),
    // },
    {
        key: 'commissionTotal',
        label: 'Ukupna provizija',
        render: (val) => formatPriceWithCurrency(val || 0),
    },
    {
        key: 'loyaltyPoints',
        label: 'Poeni',
        render: (val) => <span>{val ?? 0}</span>,
    },
    {
        key: 'status',
        label: 'Status',
        render: (val) => statusPill(val),
    },
];

const agentStatusFilterOptions = [
    { label: 'Aktivan', value: 'ACTIVE' },
    { label: 'Deaktiviran', value: 'INACTIVE' },
];

export default function AgentTable() {
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(true);

    const [filter, setFilter] = useState({ status: null });
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [deactivateModal, setDeactivateModal] = useState({ visible: false, agentId: null, loading: false });
    const [activateModal, setActivateModal] = useState({ visible: false, agentId: null, loading: false });
    const [hardDeleteModal, setHardDeleteModal] = useState({ visible: false, agentId: null, agentName: null, loading: false });

    const { canAll } = usePermissions();

    const [isAwardPointsModalOpen, setIsAwardPointsModalOpen] = useState(false);
    const [awardPointsAgent, setAwardPointsAgent] = useState(null);
    const [loadingAwardPoints, setLoadingAwardPoints] = useState(false);
    const [isDeductPointsModalOpen, setIsDeductPointsModalOpen] = useState(false);
    const [deductPointsAgent, setDeductPointsAgent] = useState(null);
    const [loadingDeductPoints, setLoadingDeductPoints] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const debouncedSearch = useDebounce(search, 500);

    const activeFilterLabel = filter.status
        ? agentStatusFilterOptions.find((o) => o.value === filter.status)?.label
        : null;

    const onSearchChange = (val) => {
        setSearch(val);
        setCurrentPage(1);
    };

    const handleViewAgentDetail = (id) => {
        navigate(`/agenti/${id}`, { state: { from: location.pathname + location.search } });
    };

    const handleEditAgent = (id) => {
        navigate(`/agenti/edit/${id}`, { state: { from: location.pathname + location.search } });
    };

    const onDeactivateAgentClick = (id) => {
        setDeactivateModal({ visible: true, agentId: id, loading: false });
    };

    const handleConfirmDeactivate = async () => {
        setDeactivateModal((prev) => ({ ...prev, loading: true }));
        try {
            const response = await agentService.deactivate(deactivateModal.agentId);

            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri deaktivaciji agenta');
                return;
            }

            toast.success('Agent je uspešno deaktiviran');
            setDeactivateModal({ visible: false, agentId: null, loading: false });
            handleFetchData();
        } catch (error) {
            console.error('Error deactivating agent:', error);
            toast.error('Greška pri deaktivaciji agenta');
        } finally {
            setDeactivateModal((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleCancelDeactivate = () => {
        setDeactivateModal({ visible: false, agentId: null, loading: false });
    };

    const onActivateAgentClick = (id) => {
        setActivateModal({ visible: true, agentId: id, loading: false });
    };

    const handleConfirmActivate = async () => {
        setActivateModal((prev) => ({ ...prev, loading: true }));
        try {
            const response = await agentService.activate(activateModal.agentId);

            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri aktivaciji agenta');
                return;
            }

            toast.success('Agent je uspešno aktiviran');
            setActivateModal({ visible: false, agentId: null, loading: false });
            handleFetchData();
        } catch (error) {
            console.error('Error activating agent:', error);
            toast.error('Greška pri aktivaciji agenta');
        } finally {
            setActivateModal((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleCancelActivate = () => {
        setActivateModal({ visible: false, agentId: null, loading: false });
    };

    const onHardDeleteAgentClick = (agent) => {
        setHardDeleteModal({
            visible: true,
            agentId: agent._id,
            agentName: `${agent.firstName || ''} ${agent.lastName || ''}`.trim(),
            loading: false,
        });
    };

    const handleConfirmHardDelete = async () => {
        setHardDeleteModal((prev) => ({ ...prev, loading: true }));
        try {
            const { success, message } = await hardDeleteUser(hardDeleteModal.agentId);

            if (!success) {
                toast.error(message || 'Greška pri trajnom brisanju agenta');
                return;
            }

            toast.success('Agent je uspešno trajno obrisan');
            setHardDeleteModal({ visible: false, agentId: null, agentName: null, loading: false });
            handleFetchData();
        } catch (error) {
            console.error('Error hard-deleting agent:', error);
            toast.error('Greška pri trajnom brisanju agenta');
        } finally {
            setHardDeleteModal((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleCancelHardDelete = () => {
        setHardDeleteModal({ visible: false, agentId: null, agentName: null, loading: false });
    };

    const handleFetchData = async () => {
        setLoading(true);
        try {
            const response = await agentService.getAll(
                currentPage,
                10,
                debouncedSearch || undefined,
                filter.status || undefined,
            );

            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri učitavanju podataka');
                setData([]);
                setTotalPages(1);
                setTotalItems(0);
                return;
            }

            const responseData = response.data || response;
            setData(responseData.items || []);
            setTotalPages(responseData.totalPages || 1);
            setTotalItems(responseData.totalItems || 0);
        } catch (error) {
            console.error('Error fetching agents:', error);
            toast.error('Greška pri učitavanju podataka');
            setData([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    const handleAwardPoints = async (points) => {
        setLoadingAwardPoints(true);
        const agentId = awardPointsAgent._id;
        const prevPoints = awardPointsAgent.loyaltyPoints ?? 0;

        setData((prev) =>
            prev.map((a) => (a._id === agentId ? { ...a, loyaltyPoints: prevPoints + points } : a))
        );
        setIsAwardPointsModalOpen(false);
        setAwardPointsAgent(null);

        const { success, message } = await adminAwardLoyaltyPoints(agentId, points);

        if (!success) {
            setData((prev) =>
                prev.map((a) => (a._id === agentId ? { ...a, loyaltyPoints: prevPoints } : a))
            );
            toast.error(message || 'Greška pri dodavanju poena');
        } else {
            toast.success('Poeni uspešno dodati');
        }

        setLoadingAwardPoints(false);
    };

    const handleDeductPoints = async (points, deductionMessage) => {
        setLoadingDeductPoints(true);
        const agentId = deductPointsAgent._id;
        const prevPoints = deductPointsAgent.loyaltyPoints ?? 0;

        setData((prev) =>
            prev.map((a) =>
                a._id === agentId ? { ...a, loyaltyPoints: Math.max(0, prevPoints - points) } : a
            )
        );
        setIsDeductPointsModalOpen(false);
        setDeductPointsAgent(null);

        const { success, message } = await adminDeductLoyaltyPoints(
            agentId,
            points,
            deductionMessage
        );

        if (!success) {
            setData((prev) =>
                prev.map((a) => (a._id === agentId ? { ...a, loyaltyPoints: prevPoints } : a))
            );
            toast.error(message || 'Greška pri oduzimanju poena');
        } else {
            toast.success('Poeni uspešno oduzeti');
        }

        setLoadingDeductPoints(false);
    };

    const handleAgentCreated = () => {
        setIsCreateModalOpen(false);
        handleFetchData();
    };

    useEffect(() => {
        handleFetchData();
        // eslint-disable-next-line
    }, [currentPage, debouncedSearch, filter.status]);

    const tableActions = [
        {
            key: 'header',
            label: 'Opcije',
            disabled: true,
            className: 'dropdown-header',
        },
        {
            key: 'view-agent-details',
            label: 'Vidi detalje',
            icon: <img src="/assets/icons/view-icon.svg" alt="View" />,
            onClick: (agent) => handleViewAgentDetail(agent._id),
        },
        {
            key: 'edit-agent',
            label: 'Uredi nalog',
            icon: <img src="/assets/icons/edit-icon.svg" alt="Edit" />,
            onClick: (agent) => handleEditAgent(agent._id),
        },
        {
            key: 'award-points',
            label: 'Dodaj poene',
            icon: <img src="/assets/icons/edit-icon.svg" alt="Award points" />,
            onClick: (agent) => {
                setAwardPointsAgent(agent);
                setIsAwardPointsModalOpen(true);
            },
        },
        {
            key: 'deduct-points',
            label: 'Oduzmi poene',
            icon: <img src="/assets/icons/edit-icon.svg" alt="Deduct points" />,
            onClick: (agent) => {
                setDeductPointsAgent(agent);
                setIsDeductPointsModalOpen(true);
            },
        },
        {
            key: 'deactivate',
            label: 'Deaktiviraj agenta',
            icon: <img src="/assets/icons/delete.svg" alt="Deactivate" />,
            className: 'delete',
            danger: true,
            onClick: (agent) => onDeactivateAgentClick(agent._id),
            visible: (agent) => agent.status === 'ACTIVE',
        },
        {
            key: 'activate',
            label: 'Aktiviraj agenta',
            icon: <img src="/assets/icons/view-icon.svg" alt="Activate" />,
            onClick: (agent) => onActivateAgentClick(agent._id),
            visible: (agent) => agent.status !== 'ACTIVE',
        },
        {
            key: 'hard-delete',
            label: 'Trajno obriši nalog',
            icon: <img src="/assets/icons/delete.svg" alt="Delete" />,
            className: 'delete',
            danger: true,
            onClick: (agent) => onHardDeleteAgentClick(agent),
            visible: () => canAll(['users:delete', 'scope:all']),
        },
    ];

    return (
        <>
            <div className={styles.totalCountRow}>
                <div className={styles.totalCount}>
                    <span className={styles.totalCount__label}>Ukupno agenata</span>
                    <span className={styles.totalCount__value}>
                        {new Intl.NumberFormat('de-DE').format(totalItems)}
                    </span>
                    {(debouncedSearch || filter.status) && (
                        <span className={styles.totalCount__hint}>(filtrirano)</span>
                    )}
                </div>
            </div>
            <div className={`${styles.wrapper} users_wrapper`}>
                <div className={styles.filters}>
                    <div className={styles.filters__left}>
                    <Search
                        placeholder="Pretraži agente"
                        className="search-input"
                        allowClear
                        onChange={(e) => onSearchChange(e.target.value)}
                        width="auto"
                    />
                        <Dropdown
                            menu={{
                                items: agentStatusFilterOptions.map((option) => ({
                                    key: option.value,
                                    label: option.label,
                                    onClick: () => {
                                        setFilter((prev) => ({ ...prev, status: option.value }));
                                        setCurrentPage(1);
                                    },
                                })),
                            }}
                            trigger={['click']}
                            placement="bottomLeft"
                            className="btn-filter-container"
                        >
                            <Button className={`btn-tertiary btn-filter ${filter.status ? 'active' : ''}`}>
                                {activeFilterLabel ? `Status: ${activeFilterLabel}` : 'Status'}
                                {filter.status && (
                                    <img
                                        src="/assets/icons/x-gray.svg"
                                        title="Ukloni filter status"
                                        style={{ width: '10px', height: '10px', marginRight: '5px', cursor: 'pointer' }}
                                        alt="Ukloni filter"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilter((prev) => ({ ...prev, status: null }));
                                            setCurrentPage(1);
                                        }}
                                    />
                                )}
                            </Button>
                        </Dropdown>
                    </div>
                    <Button
                        type="primary"
                        className="btn-primary"
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        Dodaj agenta
                    </Button>
                </div>
                {loading ? (
                    <Spinner size="lg" />
                ) : (
                    <TableSection
                        columns={tableColumns}
                        actions={tableActions}
                        data={data}
                        emptyState={{
                            icon: '/assets/icons/no-users.svg',
                            title: 'Trenutno nema agenata',
                        }}
                        showPagination={data?.length > 0}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            <CreateAgentModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={handleAgentCreated}
            />

            <BlockUserModal
                visible={deactivateModal.visible}
                onBlockUser={handleConfirmDeactivate}
                onCancel={handleCancelDeactivate}
                loading={deactivateModal.loading}
                title="Deaktiviraj agenta"
                message="Da li ste sigurni da želite da deaktivirate ovog agenta?"
                buttonText="Deaktiviraj agenta"
                cancelText="Odustani"
            />

            <BlockUserModal
                visible={activateModal.visible}
                onBlockUser={handleConfirmActivate}
                onCancel={handleCancelActivate}
                loading={activateModal.loading}
                title="Aktiviraj agenta"
                message="Da li ste sigurni da želite da aktivirate ovog agenta?"
                buttonText="Aktiviraj agenta"
                cancelText="Odustani"
            />

            <DeleteModal
                visible={hardDeleteModal.visible}
                onDelete={handleConfirmHardDelete}
                onCancel={handleCancelHardDelete}
                loading={hardDeleteModal.loading}
                title="Trajno brisanje naloga"
                message={`Da li ste sigurni da želite da trajno obrišete nalog ${hardDeleteModal.agentName}? Nalog se ne može vratiti, email postaje slobodan za novu registraciju, a istorija porudžbina ostaje sačuvana.`}
                buttonText="Trajno obriši nalog"
                cancelText="Otkaži"
            />

            <AwardLoyaltyPointsModal
                onConfirm={handleAwardPoints}
                onClose={() => {
                    setIsAwardPointsModalOpen(false);
                    setAwardPointsAgent(null);
                }}
                loading={loadingAwardPoints}
                userName={
                    awardPointsAgent
                        ? `${awardPointsAgent.firstName} ${awardPointsAgent.lastName}`
                        : ''
                }
                isOpen={isAwardPointsModalOpen}
            />
            <DeductLoyaltyPointsModal
                onConfirm={handleDeductPoints}
                onClose={() => {
                    setIsDeductPointsModalOpen(false);
                    setDeductPointsAgent(null);
                }}
                loading={loadingDeductPoints}
                userName={
                    deductPointsAgent
                        ? `${deductPointsAgent.firstName} ${deductPointsAgent.lastName}`
                        : ''
                }
                isOpen={isDeductPointsModalOpen}
            />
        </>
    );
}

AgentTable.propTypes = {};
